import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Sparkles, Ban, ExternalLink, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

interface Subscription {
  plan_id: string;
  status: "trial" | "active" | "past_due" | "canceled" | "expired" | "free";
  cycle: string | null;
  currency: string | null;
  trial_end: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  provider: string | null;
}

const daysUntil = (iso: string | null): number => {
  if (!iso) return 0;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 3600 * 1000)));
};

const statusChip = (s: Subscription) => {
  const map: Record<string, { bg: string; text: string; label: string }> = {
    trial: { bg: "bg-violet-50 border-violet-200", text: "text-violet-700", label: "Free trial" },
    active: { bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-700", label: "Active" },
    past_due: { bg: "bg-amber-50 border-amber-200", text: "text-amber-700", label: "Past due" },
    canceled: { bg: "bg-slate-100 border-slate-200", text: "text-slate-700", label: "Canceled" },
    expired: { bg: "bg-slate-100 border-slate-200", text: "text-slate-700", label: "Expired" },
    free: { bg: "bg-slate-100 border-slate-200", text: "text-slate-700", label: "Free" },
  };
  const c = map[s.status] || map.free;
  return <span className={`sg-chip ${c.bg} border ${c.text}`}>{c.label}</span>;
};

const BillingPage: React.FC = () => {
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const sessionId = sp.get("session_id");

  const sub = useQuery<Subscription>({
    queryKey: ["subscription"],
    queryFn: async () => (await api.get("/billing/subscription")).data,
  });

  // Poll status if we came back from Stripe/Razorpay success redirect
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const iv = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clearInterval(iv);
          toast.success("Payment confirmed. Your plan is now active.");
          qc.invalidateQueries({ queryKey: ["subscription"] });
          sp.delete("session_id");
          setSp(sp, { replace: true });
        } else if (attempts >= 8) {
          clearInterval(iv);
          toast.info("Still waiting on payment confirmation. Refresh in a moment.");
        }
      } catch {
        // ignore, poll again
      }
    }, 2500);
    return () => clearInterval(iv);
  }, [sessionId, qc, sp, setSp]);

  const cancel = useMutation({
    mutationFn: async () => (await api.post("/billing/cancel")).data,
    onSuccess: () => {
      toast.success("Cancellation scheduled. You'll keep access until the period ends.");
      qc.invalidateQueries({ queryKey: ["subscription"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Cancel failed"),
  });

  const s = sub.data;
  const trialDays = s?.status === "trial" ? daysUntil(s.trial_end) : 0;
  const renewalDays = s?.current_period_end ? daysUntil(s.current_period_end) : 0;

  return (
    <DashboardLayout
      title="Billing & Subscription"
      subtitle="See your current plan, renewal date and past invoices."
      seoPath="/settings/billing"
      seoDescription="Manage your SociBaba subscription — view plan, cancel, download invoices."
    >
      {sub.isLoading ? (
        <div className="sg-card p-8 flex items-center gap-2 text-sm text-[color:var(--sg-muted)]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading subscription…
        </div>
      ) : s ? (
        <>
          {/* Trial banner */}
          {s.status === "trial" && (
            <div className="sg-card p-5 mb-6 flex items-start gap-3 border-l-4 border-l-violet-500 bg-violet-50/50" data-testid="trial-banner">
              <Sparkles className="h-5 w-5 text-violet-600 shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <div className="font-semibold text-slate-800">
                  You're on the Pro trial — <b>{trialDays} day{trialDays === 1 ? "" : "s"} left</b>.
                </div>
                <div className="text-slate-600 mt-0.5">
                  Full Pro features until {s.trial_end && new Date(s.trial_end).toLocaleDateString()}.
                  No card required. Pick a plan below to keep going after your trial ends.
                </div>
              </div>
              <Link to="/pricing">
                <Button className="bg-violet-600 text-white hover:bg-violet-700 h-9" data-testid="upgrade-from-trial">
                  See plans
                </Button>
              </Link>
            </div>
          )}

          {/* Current plan card */}
          <div className="sg-card p-6" data-testid="current-plan-card">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="sg-label mb-1">Current plan</div>
                <div className="flex items-center gap-2">
                  <div className="font-display text-2xl font-bold tracking-tight capitalize">
                    {s.plan_id}
                  </div>
                  {statusChip(s)}
                  {s.cancel_at_period_end && (
                    <span className="sg-chip bg-amber-50 border-amber-200 text-amber-700">
                      <AlertCircle className="h-3 w-3" /> Cancels at period end
                    </span>
                  )}
                </div>
                {s.cycle && s.currency && (
                  <div className="text-xs text-[color:var(--sg-muted)] mt-1">
                    Billed {s.cycle} in <b>{s.currency}</b> via <b className="capitalize">{s.provider}</b>
                  </div>
                )}
              </div>
              <div className="text-right">
                {s.status === "active" && s.current_period_end && (
                  <>
                    <div className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)]">Renews in</div>
                    <div className="font-display text-2xl font-bold text-[color:var(--sg-fg)]">
                      {renewalDays} day{renewalDays === 1 ? "" : "s"}
                    </div>
                    <div className="text-[11px] text-[color:var(--sg-muted)]">
                      {new Date(s.current_period_end).toLocaleDateString()}
                    </div>
                  </>
                )}
                {(s.status === "free" || s.status === "expired") && (
                  <Link to="/pricing">
                    <Button className="bg-[color:var(--sg-fg)] text-white hover:bg-black" data-testid="upgrade-btn">
                      Upgrade
                    </Button>
                  </Link>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <Link to="/pricing">
                <Button variant="outline" size="sm" data-testid="change-plan-btn">
                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> Change plan
                </Button>
              </Link>
              {s.status === "active" && !s.cancel_at_period_end && (
                <Button
                  variant="outline" size="sm"
                  data-testid="cancel-subscription-btn"
                  disabled={cancel.isPending}
                  onClick={() => {
                    if (window.confirm("Cancel at period end? You'll keep access until then.")) {
                      cancel.mutate();
                    }
                  }}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  {cancel.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Ban className="h-3.5 w-3.5 mr-1" />}
                  Cancel
                </Button>
              )}
            </div>
          </div>

          {/* Refund policy note */}
          <div className="sg-card p-5 mt-4 text-xs text-[color:var(--sg-muted)] leading-relaxed" data-testid="refund-note">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <b className="text-[color:var(--sg-fg)]">7-day money-back guarantee.</b>{" "}
                If you're not happy within 7 days of your first paid subscription, contact us
                for a full refund. After 7 days, refunds are handled case-by-case by our team.
                See our <Link to="/refunds" className="underline">refund policy</Link>.
              </div>
            </div>
          </div>
        </>
      ) : null}
    </DashboardLayout>
  );
};

export default BillingPage;
